import React from 'react';
import { useTranslation } from 'react-i18next';

interface BenefitsDetailSectionProps {
  /** Callback invoked when the "Đăng Ký Tư Vấn Miễn Phí" button is clicked */
  onConsultClick: () => void;
}

/**
 * BenefitsDetailSection
 *
 * Renders a styled section that lists the advantages of the Latvia
 * temporary residence card and provides a call‑to‑action button.
 *
 * The component is deliberately kept simple and type‑safe:
 * - `benefits` is a `readonly` tuple so accidental mutation is prevented.
 * - The `key` for each benefit uses the index because the list is static.
 * - `onConsultClick` is required; if a consumer forgets to pass it,
 *   TypeScript will raise an error.
 *
 * Any runtime error while invoking `onConsultClick` is safely caught
 * and logged, ensuring the UI remains responsive.
 */
const BenefitsDetailSection: React.FC<BenefitsDetailSectionProps> = ({
  onConsultClick,
}) => {
  const { t } = useTranslation('latvia');

  /**
   * Static list of benefits displayed in the section.
   * Keeping this data outside the component prevents it from being
   * recreated on each render.
   */
  const benefits = [
    t('detail.benefits.1'),
    t('detail.benefits.2'),
    t('detail.benefits.3'),
    t('detail.benefits.4'),
    t('detail.benefits.5'),
    t('detail.benefits.6'),
    t('detail.benefits.7'),
    t('detail.benefits.8'),
  ];

  /** Wrapper to guard the click handler against unexpected errors */
  const handleConsultClick = () => {
    try {
      onConsultClick();
    } catch (error) {
      console.error('Error executing onConsultClick:', error);
    }
  };

  return (
    <section className="py-8 md:py-20 bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
        <div className="text-center mb-6 md:mb-16">
          <h2 className="text-lg md:text-4xl font-bold mb-1.5 md:mb-4 font-serif">
            {t('detail.benefits.title')} <strong>{t('detail.benefits.titleSub')}</strong>
          </h2>
          <p className="text-xs md:text-lg text-gray-300 max-w-2xl mx-auto">
            {t('detail.benefits.description')}
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 md:gap-6">
          {benefits.map((benefit, index) => (
            <div
              key={index}
              className="bg-white/5 backdrop-blur-sm rounded-lg md:rounded-xl p-3 md:p-6 border border-white/10 hover:bg-white/10 transition-all"
            >
              <i className="ri-check-double-line text-lg md:text-3xl text-gold-400 mb-1 md:mb-3"></i>
              <p className="text-white text-[10px] leading-relaxed md:text-sm">
                {benefit}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-6 md:mt-12 text-center">
          <button
            type="button"
            onClick={handleConsultClick}
            className="px-5 md:px-8 py-2.5 md:py-4 bg-gold-600 text-white text-xs md:text-lg font-semibold rounded-lg hover:bg-gold-700 transition-all shadow-lg whitespace-nowrap cursor-pointer"
          >
            {t('detail.benefits.button')}
          </button>
        </div>
      </div>
    </section>
  );
};

export default BenefitsDetailSection;